import React, { useState } from "react";

// Third-party
import styled from "styled-components";
import { useDispatch } from "react-redux";
import { useQuery, useMutation } from "@apollo/react-hooks";

//Utilities and constants
import { getErrorMessage } from "@sellout/ui/build/utils/ErrorUtil";
import { AppNotificationTypeEnum } from "../models/interfaces/IAppNotification";

// API calls and GraphQL
import GET_PROFILE from "@sellout/models/.dist/graphql/queries/profile.query";
import UPDATE_ORGANIZATION from "@sellout/models/.dist/graphql/mutations/updateOrganization.mutation";
import IS_SUPER_USER from "@sellout/models/.dist/graphql/queries/isSuperUser.query";
import ConnectHiveButton from "../components/ConnectHiveButton";

// Components
import Input, { InputSizes } from "@sellout/ui/build/components/Input";
import Button, { ButtonTypes, ButtonStates } from "@sellout/ui/build/components/Button";
import OrganizationLogo from "../components/OrganizationLogo";
import SelectLogo from "../components/SelectLogo";
import BooleanInput from "../elements/BooleanInput";
import Dropdown from "@sellout/ui/build/components/Dropdown";
import AddressSearchDropdown from "@sellout/ui/build/components/AddressSearchDropdown";
import RichTextEditor from "../elements/RichTextEditor";
import Error from "../elements/Error";
import PhoneNumberInput, { PhoneNumberInputSizes } from "@sellout/ui/build/components/PhoneNumberInput";
import { Page } from "../components/PageLayout";

// Types & interfaces
import IAddress from "@sellout/models/.dist/interfaces/IAddress";
import { TicketFormatAsEnum, TicketShareExpirationEnum } from "@sellout/models/.dist/interfaces/IOrganization";

// Styles
import { Colors, Icons } from "@sellout/ui";
import { media } from "@sellout/ui/build/utils/MediaQuery";

// Redux actions
import * as AppActions from "../redux/actions/app.actions";

const Container = styled.div`
  width: 274px;
`;

const SubContainer = styled.div`
  width: 572px;
  ${media.mobile`
    width: 100%;
    padding: 0 25px 0 0;
    `}
`;

const PageTitle = styled.div`
  color: ${Colors.Grey1};
  font-weight: 600;
  font-size: 2.4rem;
  margin-bottom: 25px;
`;

const Spacer = styled.div`
  height: 24px;
`;

const RichTextContainer = styled.div`
  width: 572px;
  ${media.mobile`
    width: 100%;
    padding: 0 25px 0 0;
    `}
`;
enum OrganizationSettingsLabelsEnum {
  PageTitle = "Organization Settings",
  OrganizationName = "Organization name",
  OrganizationNamePlaceholder = "Enter organization name",
  Logo = "Logo",
  UpdateLogo = "Update logo",
  CustomerSupportEmail = "Customer support email",
  CustomerSupportEmailPlaceholder = "Enter support email",
  CustomerSupportPhone = "Customer support phone number",
  Website = "Website",
  WebsitePlaceholder = "Enter website url",
  EnableSeasonTickets = "Enable Season Tickets",
  EnableTegIntegration = "Enable TEI / TEG Integration",
  EnableWeb3Integration = "Enable Web3 Integration",
  EnableHiveIntegration = "Enable Hive Integration",
  HiveClientId = "Hive Client ID",
  HiveClientIdPlaceholder = "Enter Hive Client ID",
  HiveClientSecret = "Hive Client Secret",
  HiveClientSecretPlaceholder = "Enter Hive Client Secret",
  TicketFormat = "Ticket Format",
  TicketShareExpiration = "Ticket share expiration",
  PhysicalAddress = "Physical address",
  Bio = "Bio",
  FacebookPixelId = "Facebook Pixel ID",
  FacebookPixelIdPlaceholder = "Ex. '205397968110775'",
  GoogleAnalyticsId = "Google Analytics ID",
  GoogleAnalyticsIdPlaceholder = "Ex. 'G-XXXXXXXXXX'",
  SaveChanges = "Save changes"
}


enum OrganizationTegLabelsEnum {
  EnableMemberIdValidation = "Enable Member Id Validation",
  TegClientId = "TEI Client Id",
  TegClientIdPlaceholder = "Enter Client Id",
  TegSecretId = "TEI Secret Id",
  TegSecretIdPlaceholder = "Enter Secret Id",
  TegUrl = "TEI Url",
  TegUrlPlaceholder = "Enter a url"
}

enum OrganizationFallbackEnum {
  NoOrganizationName = "No Organization Name"
}

enum OrganizationNotificationEnum {
  OrganizationSaved = "Your organization settings have been saved",
  TegRequired = "ClientId,SecretId,Url is required if you selected TEG Integeration.",
  OrgNameSpaces = "Organization name should not contain spaces."
}
type OrganizationSettingsProps = {};
const OrganizationSettings: React.FC<OrganizationSettingsProps> = () => {
  const dispatch = useDispatch();
  const { data } = useQuery(GET_PROFILE);
  const { organization } = data;
  const [name, setName] = useState(organization.orgName);
  const [email, setEmail] = useState(organization.email);
  const [orgLogoUrl, setOrgLogoUrl] = useState(organization.orgLogoUrl);
  const [phoneNumber, setPhoneNumber] = useState(organization.phoneNumber);
  const [address, setAddress] = useState(organization.address);
  const [website, setWebsite] = useState(organization.orgUrls[0]);
  const [bio, setBio] = useState(organization.bio);
  const [ticketFormat, setTicketFormat] = useState(
    organization.ticketFormat || TicketFormatAsEnum.Standard
  );
  const [ticketShareExpiration, setTicketShareExpiration] = useState(
    organization.ticketShareExpiration || TicketShareExpirationEnum.Never
  );

  const [isSeasonTickets, setIsSeasonTickets] = useState(
    organization?.isSeasonTickets as boolean
  );
  const [isTegIntegration, setIsTegIntegration] = useState(
    organization?.isTegIntegration as boolean
  );
  const [isWeb3Integration, setIsWeb3Integration] = useState(
    organization?.isWeb3Integration as boolean
  );
  const [isHiveIntegration, setIsHiveIntegration] = useState(
    organization?.hiveIntegration?.isHiveIntegration as boolean
  );

  const [hiveClientId, setHiveClientId] = useState(
    organization?.hiveIntegration?.clientId as string || ""
  );

  const [hiveClientSecret, setHiveClientSecret] = useState(
    organization?.hiveIntegration?.clientSecret as string || ""
  );

  const isHiveConnected = Boolean(organization?.hiveIntegration?.isConnected);
  const [validateMemberId, setValidateMemberId] = useState(
    organization?.validateMemberId as boolean
  );
  const [facebookPixelId, setFacebookPixelId] = useState(
    organization.facebookPixelId
  );
  const [googleAnalyticsId, setGoogleAnalyticsId] = useState(
    organization.googleAnalyticsId
  );

  const [tegClientID, setTegClientID] = useState(organization.tegClientID);

  const [tegSecret, setTegSecret] = useState(organization.tegSecret);

  const [tegURL, setTegURL] = useState(organization.tegURL);

  const [errorMsg, setErrorMsg] = useState("");

  const { data: superAdminData } = useQuery(IS_SUPER_USER);
  const [updateOrganization, { loading }] = useMutation(UPDATE_ORGANIZATION, {
    refetchQueries: [{ query: GET_PROFILE }],
    onCompleted() {
      dispatch(
        AppActions.showNotification(
          OrganizationNotificationEnum.OrganizationSaved,
          AppNotificationTypeEnum.Success
        )
      );
      setErrorMsg("")
    },
    onError(error) {
      setErrorMsg(getErrorMessage(error));
    },
  });

  const setSeasonEnable = () => {
    setIsSeasonTickets(!isSeasonTickets);
    updateOrganization({
      variables: {
        organization: {
          orgName: name || OrganizationFallbackEnum.NoOrganizationName,
          orgUrls: website,
          address,
          bio,
          email,
          phoneNumber,
          facebookPixelId,
          googleAnalyticsId,
          orgLogoUrl,
          isSeasonTickets: !isSeasonTickets,
          tegClientID,
          tegSecret,
          tegURL,
          ticketFormat,
          ticketShareExpiration,
        },
      },
    });
  };


  const setTegIntegration = () => {
    setIsTegIntegration(!isTegIntegration);
    setValidateMemberId(false);
  };

  const setWeb3Integration = () => {
    setIsWeb3Integration(!isWeb3Integration);
  };

  const setHiveIntegration = () => {
    setIsHiveIntegration(!isHiveIntegration);
  };

  const setValidateMemberIds = () => {
    setValidateMemberId((prevValidateMemberId) => !prevValidateMemberId);
  };
  return (
    <>
      <Page>
        <PageTitle>{OrganizationSettingsLabelsEnum.PageTitle}</PageTitle>
        <Container>
          <Input
            label={OrganizationSettingsLabelsEnum.OrganizationName}
            placeholder={OrganizationSettingsLabelsEnum.OrganizationNamePlaceholder}
            size={InputSizes.Regular}
            value={name}
            width="100%"
            onChange={(event: React.FormEvent<HTMLInputElement>) => {
              setName(event.currentTarget.value);
            }}
          />
          <Spacer />
          <SelectLogo
            imageUrl={orgLogoUrl}
            setImageUrl={setOrgLogoUrl}
            label={OrganizationSettingsLabelsEnum.Logo}
            UploadDisplay={<OrganizationLogo logoUrl={orgLogoUrl} />}
            uploadText={OrganizationSettingsLabelsEnum.UpdateLogo}
          />
          <Spacer />
          <Input
            label={OrganizationSettingsLabelsEnum.CustomerSupportEmail}
            placeholder={OrganizationSettingsLabelsEnum.CustomerSupportEmailPlaceholder}
            size={InputSizes.Regular}
            value={email}
            width="100%"
            onChange={(event: React.FormEvent<HTMLInputElement>) => {
              setEmail(event.currentTarget.value);
            }}
            icon={Icons.EnvelopeLight}
          />
          <Spacer />
          <PhoneNumberInput
            label={OrganizationSettingsLabelsEnum.CustomerSupportPhone}
            phoneNumberInputSize={PhoneNumberInputSizes.Regular}
            value={phoneNumber}
            onChange={(val: string) => {
              setPhoneNumber(val);
            }}
          />
          <Spacer />
          <Input
            label={OrganizationSettingsLabelsEnum.Website}
            placeholder={OrganizationSettingsLabelsEnum.WebsitePlaceholder}
            size={InputSizes.Regular}
            width="100%"
            value={website ? website : ""}
            onChange={(event: React.FormEvent<HTMLInputElement>) => {
              setWebsite(event.currentTarget.value);
            }}
            icon={Icons.LinkLight}
          />
          <Spacer />
          <Dropdown
            value={ticketShareExpiration}
            width="100%"
            items={[
              {
                text: TicketShareExpirationEnum.Never,
                value: TicketShareExpirationEnum.Never,
              },
              {
                text: TicketShareExpirationEnum.FortyEightHours,
                value: TicketShareExpirationEnum.FortyEightHours,
              },
              {
                text: TicketShareExpirationEnum.NinetySixHours,
                value: TicketShareExpirationEnum.NinetySixHours,
              },
            ]}
            onChange={(item: any) => {
              setTicketShareExpiration(item?.value ?? item);
            }}
            label={OrganizationSettingsLabelsEnum.TicketShareExpiration}
          />
        </Container>
        {superAdminData?.isSuperUser && (
          <>
            {" "}
            <Spacer />
            <BooleanInput
              active={isSeasonTickets}
              onChange={() => setSeasonEnable()}
              label={OrganizationSettingsLabelsEnum.EnableSeasonTickets}
            />
            <Spacer />
            <BooleanInput
              active={isTegIntegration}
              onChange={() => setTegIntegration()}
              label={OrganizationSettingsLabelsEnum.EnableTegIntegration}
            />

            {isTegIntegration &&
              <>
                <Spacer />
                <BooleanInput
                  active={validateMemberId}
                  onChange={() => setValidateMemberIds()}
                  label={OrganizationTegLabelsEnum.EnableMemberIdValidation}
                />
              </>
            }
            {isTegIntegration && (
              <>
                <Spacer />
                <Input
                  label={OrganizationTegLabelsEnum.TegClientId}
                  placeholder={OrganizationTegLabelsEnum.TegClientIdPlaceholder}
                  size={InputSizes.Regular}
                  value={tegClientID}
                  subLabel={"(required)"}
                  width="350px"
                  onChange={(event: React.FormEvent<HTMLInputElement>) => {
                    setTegClientID(event.currentTarget.value);
                  }}
                />
                <Spacer />
                <Input
                  label={OrganizationTegLabelsEnum.TegSecretId}
                  placeholder={OrganizationTegLabelsEnum.TegSecretIdPlaceholder}
                  size={InputSizes.Regular}
                  value={tegSecret}
                  width="350px"
                  subLabel={"(required)"}
                  onChange={(event: React.FormEvent<HTMLInputElement>) => {
                    setTegSecret(event.currentTarget.value);
                  }}
                />
                <Spacer />
                <Input
                  label={OrganizationTegLabelsEnum.TegUrl}
                  placeholder={OrganizationTegLabelsEnum.TegUrlPlaceholder}
                  size={InputSizes.Regular}
                  value={tegURL}
                  subLabel={"(required)"}
                  width="350px"
                  onChange={(event: React.FormEvent<HTMLInputElement>) => {
                    setTegURL(event.currentTarget.value);
                  }}
                />
              </>
            )}
            <Spacer />
            <BooleanInput
              active={isWeb3Integration}
              onChange={() => setWeb3Integration()}
              label={OrganizationSettingsLabelsEnum.EnableWeb3Integration}
            />
            <Spacer />
            <Container>
              <Dropdown
                value={ticketFormat}
                width="100%"
                items={[
                  {
                    text: TicketFormatAsEnum.Standard,
                    value: TicketFormatAsEnum.Standard,
                  },
                  {
                    text: TicketFormatAsEnum.RCSC,
                    value: TicketFormatAsEnum.RCSC,
                  },
                ]}
                onChange={(item: any) => {
                  setTicketFormat(item);
                }}
                label={OrganizationSettingsLabelsEnum.TicketFormat}
              />
            </Container>
          </>
        )}

        <Spacer />
        <BooleanInput
          active={isHiveIntegration}
          onChange={() => setHiveIntegration()}
          label={OrganizationSettingsLabelsEnum.EnableHiveIntegration}
        />
        {isHiveIntegration && (
          <>
            <Spacer />
            <Input
              label={OrganizationSettingsLabelsEnum.HiveClientId}
              placeholder={isHiveConnected ? "••••••••••••••••••••••••" : OrganizationSettingsLabelsEnum.HiveClientIdPlaceholder}
              size={InputSizes.Regular}
              value={hiveClientId}
              width="350px"
              onChange={(event: React.FormEvent<HTMLInputElement>) => {
                setHiveClientId(event.currentTarget.value);
              }}
            />
            <Spacer />
            <Input
              label={OrganizationSettingsLabelsEnum.HiveClientSecret}
              placeholder={isHiveConnected ? "••••••••••••••••••••••••" : OrganizationSettingsLabelsEnum.HiveClientSecretPlaceholder}
              size={InputSizes.Regular}
              value={hiveClientSecret}
              width="350px"
              onChange={(event: React.FormEvent<HTMLInputElement>) => {
                setHiveClientSecret(event.currentTarget.value);
              }}
            />
            <Spacer />
            <ConnectHiveButton isConnected={isHiveConnected} clientId={hiveClientId} clientSecret={hiveClientSecret} />
          </>
        )}

        <Spacer />
        <SubContainer>
          <AddressSearchDropdown
            label={OrganizationSettingsLabelsEnum.PhysicalAddress}
            value={address as IAddress}
            onChange={setAddress}
            width="572px"
          />
        </SubContainer>
        <Spacer />
        <RichTextContainer>
          <RichTextEditor
            value={bio}
            onChange={(html: string) => setBio(html)}
            label={OrganizationSettingsLabelsEnum.Bio}
          />
        </RichTextContainer>
        <Spacer />
        <Container>
          <Input
            label={OrganizationSettingsLabelsEnum.FacebookPixelId}
            placeholder={OrganizationSettingsLabelsEnum.FacebookPixelIdPlaceholder}
            size={InputSizes.Regular}
            value={facebookPixelId}
            width="100%"
            onChange={(event: React.FormEvent<HTMLInputElement>) => {
              setFacebookPixelId(event.currentTarget.value);
            }}
            icon={Icons.FacebookPlain}
          />
          <Spacer />
          <Input
            label={OrganizationSettingsLabelsEnum.GoogleAnalyticsId}
            placeholder={OrganizationSettingsLabelsEnum.GoogleAnalyticsIdPlaceholder}
            size={InputSizes.Regular}
            value={googleAnalyticsId}
            width="100%"
            onChange={(event: React.FormEvent<HTMLInputElement>) => {
              setGoogleAnalyticsId(event.currentTarget.value);
            }}
            icon={Icons.Google}
          />
        </Container>
        <Spacer />
        {errorMsg && (
          <>
            <Error margin="10px 0px 0px 0px">{errorMsg}</Error>
            <Spacer />
          </>
        )}
        <Button
          type={ButtonTypes.Thin}
          state={ButtonStates.Active}
          text={OrganizationSettingsLabelsEnum.SaveChanges}
          loading={loading}
          onClick={() => {
            if (isTegIntegration && (!tegClientID || !tegSecret || !tegURL)) {
              dispatch(
                AppActions.showNotification(
                  OrganizationNotificationEnum.TegRequired,
                  AppNotificationTypeEnum.Warning
                )
              );
            }
            const trimmedName = name.trim();
            const pattern = /^[^\s].*$/
            if (!pattern.test(trimmedName)) {
              dispatch(
                AppActions.showNotification(
                  OrganizationNotificationEnum.OrgNameSpaces,
                  AppNotificationTypeEnum.Warning
                )
              );
              return false;
            }
            else {
              updateOrganization({
                variables: {
                  organization: {
                    orgName: name.trim() || OrganizationFallbackEnum.NoOrganizationName,
                    orgUrls: website,
                    address,
                    bio,
                    email,
                    phoneNumber,
                    facebookPixelId,
                    googleAnalyticsId,
                    orgLogoUrl,
                    isSeasonTickets,
                    validateMemberId,
                    isTegIntegration,
                    isWeb3Integration,
                    hiveIntegration: {
                      isHiveIntegration: isHiveIntegration,
                      clientId: hiveClientId,
                      clientSecret: hiveClientSecret,
                    },
                    tegClientID,
                    tegSecret,
                    tegURL,
                    ticketFormat,
                    ticketShareExpiration,
                  },
                },
              });
            }
          }}
        />
        <Spacer />
      </Page>
    </>
  );
};

export default OrganizationSettings;
